import { CustomTooltip, Icon } from '@tcweb/connect-ui';
import {
  MeasurementInfoButtonConfig,
  SlopeFormat,
} from '@trimble/web3d-providers';
import { type JSX, createMemo, Show, For } from 'solid-js';
import { BasicButton } from '@trimble/ui-components/solidjs';

export interface DropdownOption {
  label: string;
  value: string;
}

interface ResultHeaderProps {
  icon: string;
  title: string;
  testId?: string;
  dropdownOptions?: DropdownOption[];
  selectedDropdownValue?: SlopeFormat;
  onDropdownChange?(value: SlopeFormat): void;
  infoButtonConfig?: MeasurementInfoButtonConfig;
  disableDropdown?: boolean;
  disabledReason?: string;
}

export const ResultHeader = (props: ResultHeaderProps): JSX.Element => {
  const handleOptionSelect = (event: CustomEvent): void => {
    const menu = (event.target as HTMLElement)?.closest(
      'modus-wc-dropdown-menu'
    );
    if (menu) {
      menu.menuVisible = false;
    }
    props.onDropdownChange?.(event.detail.value);
  };

  const selectedOption = createMemo(() =>
    props.dropdownOptions?.find(
      (option) => option.value === props.selectedDropdownValue
    )
  );

  return (
    <div
      class="font-semibold text-gray-7 flex items-center mb-1 justify-between"
      data-testid={props.testId}
    >
      <div class="flex items-center gap-2">
        <Icon icon={props.icon} size="icon-small" />
        {props.title}
        <Show when={props.infoButtonConfig?.action}>
          <CustomTooltip
            text={props.infoButtonConfig!.tooltip}
            position="top-start"
            arrow={true}
          >
            <BasicButton
              iconName="tc-icon-info"
              size="small"
              action={props.infoButtonConfig!.action}
            />
          </CustomTooltip>
        </Show>
      </div>

      <Show when={props.dropdownOptions && props.dropdownOptions.length > 0}>
        <modus-wc-tooltip
          content={props.disabledReason}
          position="bottom"
          disabled={!props.disableDropdown}
        >
          <modus-wc-dropdown-menu
            button-variant="borderless"
            button-color="tertiary"
            menu-placement="bottom-end"
            disabled={props.disableDropdown}
            class={`${props.disableDropdown ? 'cursor-not-allowed' : ''}`}
          >
            <div
              slot="button"
              class="flex items-center py-1 text-xs font-xsmall"
            >
              <span class="text-gray-6">
                {selectedOption()?.label || 'Select option'}
              </span>
              <modus-wc-icon name="expand_more" size="sm"></modus-wc-icon>
            </div>

            <div slot="menu">
              <For each={props.dropdownOptions ?? []}>
                {(option) => (
                  <modus-wc-menu-item
                    class="text-gray-7"
                    size="md"
                    label={option.label}
                    value={option.value}
                    on:itemSelect={handleOptionSelect}
                    selected={option.value === props.selectedDropdownValue}
                  />
                )}
              </For>
            </div>
          </modus-wc-dropdown-menu>
        </modus-wc-tooltip>
      </Show>
    </div>
  );
};
